import type {
  BrandStats,
  PerModelStat,
  PerPromptStat,
  TopCompetitor,
  SentimentBreakdown,
} from '@ekorank/shared'
import { modelLabel } from './modelDisplay'

export type InsightSeverity = 'good' | 'warn' | 'info'

export interface InsightCard {
  id: string
  severity: InsightSeverity
  title: string
  body: string
}

interface DeriveInput {
  stats: BrandStats
  perModel: PerModelStat[]
  perPrompt: PerPromptStat[]
  competitors: TopCompetitor[]
  sentiment: SentimentBreakdown
}

/**
 * Pure function: turns aggregated stats into plain-language insight cards.
 * No LLM call; rule-based so output is deterministic and free.
 */
export function deriveInsights(input: DeriveInput): InsightCard[] {
  const cards: InsightCard[] = []
  const { stats, perModel, perPrompt, competitors, sentiment } = input

  // 1. WoW trend on mention rate
  if (stats.deltas) {
    const delta = stats.deltas.mentionRatePct
    if (delta >= 5) {
      cards.push({
        id: 'trend-up',
        severity: 'good',
        title: `Mention rate up ${delta} points`,
        body: `You're being mentioned more often this period than last (${stats.previous?.mentionRate ?? 0}% → ${stats.current.mentionRate}%).`,
      })
    } else if (delta <= -5) {
      cards.push({
        id: 'trend-down',
        severity: 'warn',
        title: `Mention rate down ${Math.abs(delta)} points`,
        body: `You went from ${stats.previous?.mentionRate ?? 0}% to ${stats.current.mentionRate}% mention rate. Worth investigating which prompts dropped.`,
      })
    }
  }

  // 2. Best vs worst model
  if (perModel.length >= 2) {
    const sorted = [...perModel].sort((a, b) => b.mentionRate - a.mentionRate)
    const best = sorted[0]!
    const worst = sorted[sorted.length - 1]!
    if (best.mentionRate - worst.mentionRate >= 20) {
      cards.push({
        id: 'model-gap',
        severity: 'info',
        title: `${modelLabel(best.model)} mentions you ${best.mentionRate}%, ${modelLabel(worst.model)} only ${worst.mentionRate}%`,
        body: `Your visibility varies a lot by AI engine. Consider tailoring content for the engines that aren't picking you up.`,
      })
    }
  }

  // 3. Weak prompts (zero mentions)
  const zeroPrompts = perPrompt.filter((p) => p.mentions === 0 && p.checks > 0)
  if (zeroPrompts.length > 0) {
    const sample = zeroPrompts.slice(0, 3).map((p) => `"${p.promptText}"`).join(', ')
    cards.push({
      id: 'weak-prompts',
      severity: 'warn',
      title: `${zeroPrompts.length} prompt${zeroPrompts.length === 1 ? '' : 's'} never returned a mention`,
      body: `${sample}${zeroPrompts.length > 3 ? `, +${zeroPrompts.length - 3} more` : ''}. These are visibility gaps — competitors are probably winning these queries.`,
    })
  }

  // 4. Top competitor threat
  const topComp = competitors[0]
  if (topComp && topComp.sharePct > stats.current.mentionRate) {
    cards.push({
      id: 'top-competitor',
      severity: 'warn',
      title: `${topComp.name} appears in ${topComp.sharePct}% of your checks`,
      body: `That's more often than you (${stats.current.mentionRate}%). They're winning the visibility battle for your prompts.`,
    })
  } else if (topComp) {
    cards.push({
      id: 'top-competitor',
      severity: 'info',
      title: `Top competitor: ${topComp.name} (${topComp.sharePct}%)`,
      body: `Mentioned in ${topComp.mentionCount} check${topComp.mentionCount === 1 ? '' : 's'}. You're at ${stats.current.mentionRate}%.`,
    })
  }

  // 5. Sentiment skew
  const totalScored = sentiment.positive + sentiment.neutral + sentiment.negative
  if (totalScored > 0) {
    const positivePct = Math.round((sentiment.positive / totalScored) * 100)
    const negativePct = Math.round((sentiment.negative / totalScored) * 100)
    if (negativePct >= 30) {
      cards.push({
        id: 'sentiment-negative',
        severity: 'warn',
        title: `${negativePct}% of mentions are negative`,
        body: `That's high. Review the negative responses to understand what AI engines are saying about you.`,
      })
    } else if (positivePct >= 70) {
      cards.push({
        id: 'sentiment-positive',
        severity: 'good',
        title: `${positivePct}% of mentions are positive`,
        body: `AI engines describe you favorably when they mention you.`,
      })
    }
  }

  return cards
}
